import React, { useState } from "react";
import styled from "styled-components";
import { format } from 'date-fns';
import {
    AiOutlineClose,
    AiOutlineCalendar,
    AiOutlineClockCircle,
    AiOutlineUser,
    AiOutlineEdit,
    AiOutlineBarChart,
    AiOutlineCheckCircle,
    AiOutlineWarning
} from "react-icons/ai";

const LogDetailsModal = ({ log, onClose }) => {
    const [activeTab, setActiveTab] = useState("activities");

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'MMMM dd, yyyy');
        } catch (e) {
            return dateString;
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return "N/A";
        return timeString;
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return <AiOutlineCheckCircle className="completed" />;
            case 'in progress':
                return <AiOutlineClockCircle className="in-progress" />;
            case 'delayed':
                return <AiOutlineWarning className="delayed" />;
            case 'cancelled':
                return <AiOutlineClose className="cancelled" />;
            default:
                return null;
        }
    };

    const getShiftBadgeClass = (shift) => {
        switch (shift?.toLowerCase()) {
            case 'morning': return 'morning';
            case 'evening': return 'evening';
            case 'night': return 'night';
            default: return '';
        }
    };

    return (
        <ModalOverlay>
            <ModalContainer>
                <ModalHeader>
                    <h2>Daily Log Details</h2>
                    <CloseButton onClick={onClose}>
                        <AiOutlineClose />
                    </CloseButton>
                </ModalHeader>

                <LogSummary>
                    <SummaryItem>
                        <SummaryIcon>
                            <AiOutlineCalendar />
                        </SummaryIcon>
                        <SummaryContent>
                            <SummaryLabel>Date</SummaryLabel>
                            <SummaryValue>{formatDate(log.log_date)}</SummaryValue>
                        </SummaryContent>
                    </SummaryItem>

                    <SummaryItem>
                        <SummaryIcon>
                            <AiOutlineClockCircle />
                        </SummaryIcon>
                        <SummaryContent>
                            <SummaryLabel>Shift</SummaryLabel>
                            <SummaryValue>
                                <ShiftBadge className={getShiftBadgeClass(log.shift)}>
                                    {log.shift}
                                </ShiftBadge>
                            </SummaryValue>
                        </SummaryContent>
                    </SummaryItem>

                    <SummaryItem>
                        <SummaryIcon>
                            <AiOutlineUser />
                        </SummaryIcon>
                        <SummaryContent>
                            <SummaryLabel>Supervisor</SummaryLabel>
                            <SummaryValue>{log.supervisor_name || "N/A"}</SummaryValue>
                        </SummaryContent>
                    </SummaryItem>

                    <SummaryItem>
                        <SummaryIcon>
                            <AiOutlineBarChart />
                        </SummaryIcon>
                        <SummaryContent>
                            <SummaryLabel>Activities</SummaryLabel>
                            <SummaryValue>{log.activities?.length || 0} activities</SummaryValue>
                        </SummaryContent>
                    </SummaryItem>
                </LogSummary>

                {log.notes && (
                    <NotesSection>
                        <h3>Notes</h3>
                        <NotesContent>{log.notes}</NotesContent>
                    </NotesSection>
                )}

                <TabsContainer>
                    <TabButton
                        active={activeTab === "activities"}
                        onClick={() => setActiveTab("activities")}
                    >
                        Activities
                    </TabButton>
                    <TabButton
                        active={activeTab === "issues"}
                        onClick={() => setActiveTab("issues")}
                    >
                        Issues
                    </TabButton>
                </TabsContainer>

                <TabContent>
                    {activeTab === "activities" ? (
                        <>
                            {log.activities && log.activities.length > 0 ? (
                                <ActivityList>
                                    {log.activities.map((activity, index) => (
                                        <ActivityCard key={index}>
                                            <ActivityHeader>
                                                <ActivityTitle>
                                                    <span>{activity.activity_type}</span>
                                                    {activity.product_name && (
                                                        <ProductTag>{activity.product_name}</ProductTag>
                                                    )}
                                                </ActivityTitle>
                                                <StatusBadge className={activity.status?.toLowerCase()}>
                                                    {getStatusIcon(activity.status)}
                                                    {activity.status}
                                                </StatusBadge>
                                            </ActivityHeader>

                                            <ActivityDetails>
                                                <DetailItem>
                                                    <DetailLabel>Quantity:</DetailLabel>
                                                    <DetailValue>{activity.quantity || 0}</DetailValue>
                                                </DetailItem>
                                                <DetailItem>
                                                    <DetailLabel>Workers:</DetailLabel>
                                                    <DetailValue>{activity.worker_count || 1}</DetailValue>
                                                </DetailItem>
                                                <DetailItem>
                                                    <DetailLabel>Start Time:</DetailLabel>
                                                    <DetailValue>{formatTime(activity.start_time)}</DetailValue>
                                                </DetailItem>
                                                <DetailItem>
                                                    <DetailLabel>End Time:</DetailLabel>
                                                    <DetailValue>{formatTime(activity.end_time)}</DetailValue>
                                                </DetailItem>
                                            </ActivityDetails>

                                            {activity.issues && (
                                                <ActivityIssues>
                                                    <IssuesTitle>Issues/Notes:</IssuesTitle>
                                                    <IssuesContent>{activity.issues}</IssuesContent>
                                                </ActivityIssues>
                                            )}
                                        </ActivityCard>
                                    ))}
                                </ActivityList>
                            ) : (
                                <EmptyState>No activities recorded for this log</EmptyState>
                            )}
                        </>
                    ) : (
                        <>
                            {log.activities && log.activities.some(a => a.issues) ? (
                                <IssuesList>
                                    {log.activities
                                        .filter(activity => activity.issues)
                                        .map((activity, index) => (
                                            <IssueCard key={index}>
                                                <IssueHeader>
                                                    <IssueActivity>{activity.activity_type}</IssueActivity>
                                                    <StatusBadge className={activity.status?.toLowerCase()}>
                                                        {getStatusIcon(activity.status)}
                                                        {activity.status}
                                                    </StatusBadge>
                                                </IssueHeader>
                                                <IssueDescription>{activity.issues}</IssueDescription>
                                            </IssueCard>
                                        ))
                                    }
                                </IssuesList>
                            ) : (
                                <EmptyState>No issues reported for this log</EmptyState>
                            )}
                        </>
                    )}
                </TabContent>

                <ModalFooter>
                    <FooterText>
                        <span>Created: {formatDate(log.created_at)}</span>
                        {log.updated_at && log.updated_at !== log.created_at && (
                            <span>Last Updated: {formatDate(log.updated_at)}</span>
                        )}
                    </FooterText>
                    <CloseModalButton onClick={onClose}>Close</CloseModalButton>
                </ModalFooter>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default LogDetailsModal;

// Styled Components
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #eee;
    
    h2 {
        font-size: 18px;
        margin: 0;
        color: #333;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    color: #777;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #000;
    }
`;

const LogSummary = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 15px;
    padding: 20px;
    background-color: #f9f9f9;
`;

const SummaryItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
`;

const SummaryIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: white;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    font-size: 16px;
    color: #555;
`;

const SummaryContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const SummaryLabel = styled.div`
    font-size: 12px;
    color: #777;
    margin-bottom: 3px;
`;

const SummaryValue = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: #333;
`;

const ShiftBadge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    
    &.morning {
        background-color: #e3f2fd;
        color: #1976d2;
    }
    
    &.evening {
        background-color: #fff8e1;
        color: #ff8f00;
    }
    
    &.night {
        background-color: #ede7f6;
        color: #5e35b1;
    }
`;

const NotesSection = styled.div`
    padding: 0 20px 20px;
    
    h3 {
        font-size: 16px;
        color: #333;
        margin: 0 0 10px 0;
    }
`;

const NotesContent = styled.div`
    background-color: white;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 12px 15px;
    font-size: 14px;
    color: #555;
    line-height: 1.5;
`;

const TabsContainer = styled.div`
    display: flex;
    border-bottom: 1px solid #eee;
    padding: 0 20px;
`;

const TabButton = styled.button`
    background: none;
    border: none;
    padding: 12px 15px;
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.active ? '#000' : '#777'};
    cursor: pointer;
    position: relative;
    
    &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        background-color: ${props => props.active ? '#000' : 'transparent'};
    }
    
    &:hover {
        color: #000;
    }
`;

const TabContent = styled.div`
    padding: 20px;
    flex: 1;
`;

const ActivityList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const ActivityCard = styled.div`
    background-color: white;
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const ActivityHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
`;

const ActivityTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 15px;
    color: #333;
`;

const ProductTag = styled.span`
    background-color: #f5f5f5;
    color: #666;
    font-size: 12px;
    font-weight: 400;
    padding: 2px 8px;
    border-radius: 12px;
`;

const StatusBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 4px;
    
    svg {
        font-size: 14px;
    }
    
    &.completed {
        background-color: #e8f5e9;
        color: #388e3c;
        
        svg {
            color: #4caf50;
        }
    }
    
    &.in.progress {
        background-color: #e3f2fd;
        color: #1976d2;
        
        svg {
            color: #2196f3;
        }
    }
    
    &.delayed {
        background-color: #fff3e0;
        color: #e65100;
        
        svg {
            color: #ff9800;
        }
    }
    
    &.cancelled {
        background-color: #ffebee;
        color: #c62828;
        
        svg {
            color: #f44336;
        }
    }
`;

const ActivityDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    margin-bottom: 15px;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const DetailLabel = styled.div`
    font-size: 12px;
    color: #777;
`;

const DetailValue = styled.div`
    font-size: 14px;
    color: #333;
`;

const ActivityIssues = styled.div`
    background-color: #fafafa;
    border-radius: 6px;
    padding: 10px 12px;
`;

const IssuesTitle = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #555;
    margin-bottom: 5px;
`;

const IssuesContent = styled.div`
    font-size: 13px;
    color: #666;
    line-height: 1.4;
`;

const IssuesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const IssueCard = styled.div`
    background-color: #fff3e0;
    border-left: 3px solid #ff9800;
    border-radius: 6px;
    padding: 15px;
`;

const IssueHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`;

const IssueActivity = styled.div`
    font-weight: 600;
    font-size: 14px;
    color: #333;
`;

const IssueDescription = styled.div`
    font-size: 14px;
    color: #555;
    line-height: 1.5;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 30px;
    color: #777;
    font-style: italic;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    border-top: 1px solid #eee;
    margin-top: auto;
`;

const FooterText = styled.div`
    font-size: 12px;
    color: #777;
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const CloseModalButton = styled.button`
    background-color: #f5f5f5;
    color: #333;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    cursor: pointer;
    
    &:hover {
        background-color: #e0e0e0;
    }
`;